import { useEffect, useMemo, useState, type FC, type PointerEvent } from 'react';
import { Flex, Popover, Select, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { blue, cyan, geekblue, gold, green, purple, red, yellow } from '@ant-design/colors';
import { Button, Modal } from '@g2u/ui-kit';
import { eachDayOfInterval, endOfMonth, format, startOfMonth } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Link, useNavigate } from 'react-router';
import he from 'he';

import { AwayStatusModal, RecurringAvailabilityModal } from 'components';
import { AvailabilityCalendarJob, AvailabilityCalendarSlot, AvailabilityStatus, Technician } from 'types';
import homeIcon from 'assets/images/availability/home.svg';
import callIcon from 'assets/images/availability/call.svg';
import repeatIcon from 'assets/images/availability/repeat.svg';
import {
  formatPopoverDateTime,
  hasJobPopover,
  isSlotEligible,
  nextAvailabilityStatus,
  SlotAppearance,
  slotAppearance,
  slotJob,
  truncateDescription,
} from '../helpers/availability';
import { useTechnicianAvailabilityCalendar } from '../hooks/useTechnicianAvailabilityCalendar';
import styles from './technician-availability.module.scss';

type Props = Readonly<{
  technician: Technician;
}>;

type TimeRow = {
  time: string;
};

type DragSelection = {
  date: string;
  anchor: string;
  current: string;
};

type PendingChange = {
  slots: string[];
  status: AvailabilityStatus;
};

const STATUS_LABELS: Partial<Record<AvailabilityStatus, string>> = {
  [AvailabilityStatus.Available]: 'available',
  [AvailabilityStatus.RequiresConfirmation]: 'requires confirmation',
  [AvailabilityStatus.Unavailable]: 'unavailable',
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: format(new Date(2000, index, 1), 'MMMM'),
}));

const UNAVAILABLE_COLOUR = 'rgba(0,0,0,0.15)';

const TILE_COLOURS: Record<SlotAppearance, string> = {
  [SlotAppearance.Available]: '#fff',
  [SlotAppearance.Unavailable]: UNAVAILABLE_COLOUR,
  [SlotAppearance.Away]: red[5],
  [SlotAppearance.RequiresConfirmation]: cyan[5],
  [SlotAppearance.RecurringUnavailable]: UNAVAILABLE_COLOUR,
  [SlotAppearance.RecurringRequiresConfirmation]: cyan[5],
  [SlotAppearance.OnsiteJob]: green[5],
  [SlotAppearance.RemoteJob]: geekblue[5],
  [SlotAppearance.PendingJob]: yellow[6],
  [SlotAppearance.WarrantyJob]: purple[5],
  [SlotAppearance.HardwareReturn]: gold[6],
};

const TILE_ICONS: Partial<Record<SlotAppearance, { src: string; size: number }>> = {
  [SlotAppearance.OnsiteJob]: { src: homeIcon, size: 15 },
  [SlotAppearance.PendingJob]: { src: homeIcon, size: 15 },
  [SlotAppearance.WarrantyJob]: { src: homeIcon, size: 15 },
  [SlotAppearance.HardwareReturn]: { src: homeIcon, size: 15 },
  [SlotAppearance.RemoteJob]: { src: callIcon, size: 15 },
  [SlotAppearance.RecurringUnavailable]: { src: repeatIcon, size: 24 },
  [SlotAppearance.RecurringRequiresConfirmation]: { src: repeatIcon, size: 24 },
};

const LEGEND = [
  {
    title: 'Availability Status:',
    items: [
      { appearance: SlotAppearance.Available, label: 'Available' },
      { appearance: SlotAppearance.Unavailable, label: 'Unavailable' },
      { appearance: SlotAppearance.Away, label: 'Away' },
      { appearance: 'recurring', label: 'Recurring' },
      { appearance: SlotAppearance.RequiresConfirmation, label: 'Requires Confirmation' },
    ],
  },
  {
    title: 'Job Types:',
    items: [
      { appearance: SlotAppearance.OnsiteJob, label: 'On-site Job' },
      { appearance: SlotAppearance.RemoteJob, label: 'Remote Job' },
      { appearance: SlotAppearance.PendingJob, label: 'Pending Job' },
      { appearance: SlotAppearance.WarrantyJob, label: 'Warranty Job' },
      { appearance: SlotAppearance.HardwareReturn, label: 'Hardware Return' },
    ],
  },
];

const BORDERED_LEGEND_SWATCHES: string[] = [SlotAppearance.Available, SlotAppearance.Unavailable];

const LEGEND_ITEM_WIDTHS: Record<string, number> = {
  Recurring: 130,
  'Requires Confirmation': 180,
  'Warranty Job': 130,
  'Hardware Return': 180,
};

const JobPopoverContent: FC<{ job: AvailabilityCalendarJob; technician: Technician }> = ({ job, technician }) => {
  const customer = [he.decode(job.customer_name), he.decode(job.customer_address)].filter(Boolean).join(', ');
  const contactNumbers = [
    technician.phone && `H: ${technician.phone}`,
    technician.contact1_mobile && `M: ${technician.contact1_mobile}`,
  ]
    .filter(Boolean)
    .join(', ');

  const fields = [
    { label: 'Date Booked:', value: formatPopoverDateTime(job.booked_at) },
    { label: 'Appointment:', value: formatPopoverDateTime(job.appointment) },
    { label: 'Customer:', value: customer },
    { label: 'Job Description:', value: truncateDescription(he.decode(job.job_description)) },
    { label: 'Hours Block:', value: job.block.toFixed(2) },
    { label: 'Technician Contact Number:', value: contactNumbers },
  ];

  return (
    <div className={styles.availability__popover} data-testid="job-popover">
      <Link to={`/job/edit/${job.job_id}`} className={styles.availability__popoverTitle}>
        Job # {job.job_id}
      </Link>
      {fields.map((field) => (
        <div key={field.label}>
          <div className={styles.availability__popoverLabel}>{field.label}</div>
          <div className={styles.availability__popoverValue}>{field.value}</div>
        </div>
      ))}
    </div>
  );
};

const Tile: FC<{ appearance: SlotAppearance }> = ({ appearance }) => {
  const icon = TILE_ICONS[appearance];

  return (
    <div className={styles.availability__tile} style={{ background: TILE_COLOURS[appearance] }} data-appearance={appearance}>
      {icon && <img src={icon.src} width={icon.size} height={icon.size} alt="" />}
    </div>
  );
};

export const TechnicianAvailability: FC<Props> = ({ technician }) => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [year, setYear] = useState(() => new Date().getFullYear());

  const [isAwayStatusModalOpen, setIsAwayStatusModalOpen] = useState(false);
  const [selection, setSelection] = useState<DragSelection | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);

  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);

  const {
    calendar,
    isCalendarLoading,
    updateSlots,
    isSlotsUpdating,
    createAwayStatus,
    isAwayStatusCreating,
    createRecurringAvailability,
    isRecurringAvailabilityCreating,
  } = useTechnicianAvailabilityCalendar(String(technician.ID), month, year);

  const handleRecurringAvailabilitySubmit = (values: RecurringAvailabilityModal.Payload) => {
    createRecurringAvailability(
      { technicianId: technician.ID, ...values },
      { onSuccess: () => setIsRecurringModalOpen(false) },
    );
  };

  const handleAwayStatusSubmit = (values: AwayStatusModal.Payload) => {
    createAwayStatus(
      { technicianId: technician.ID, ...values },
      { onSuccess: () => setIsAwayStatusModalOpen(false) },
    );
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();

    return [currentYear - 1, currentYear, currentYear + 1].map((value) => ({ value, label: String(value) }));
  }, []);

  const days = useMemo(() => {
    const firstDay = startOfMonth(new Date(year, month - 1, 1));

    return eachDayOfInterval({ start: firstDay, end: endOfMonth(firstDay) });
  }, [month, year]);

  const today = calendar ? format(toZonedTime(new Date(), calendar.timezone), 'yyyy-MM-dd') : null;

  const rows: TimeRow[] = useMemo(() => {
    if (!calendar?.slots) {
      return [];
    }

    const timeSet = new Set<string>();
    Object.values(calendar.slots).forEach((daySlots) => {
      Object.keys(daySlots).forEach((time) => timeSet.add(time));
    });

    return Array.from(timeSet)
      .sort()
      .map((time) => ({ time }));
  }, [calendar]);

  const isSaving = isSlotsUpdating || isAwayStatusCreating || isRecurringAvailabilityCreating;

  const isEligible = (date: string, time: string) => {
    const slot = calendar?.slots[date]?.[time];

    return !!calendar && !!slot && isSlotEligible(slot, `${date} ${time}`, calendar.timezone);
  };

  const selectedTimes = (dragSelection: DragSelection) => {
    const anchorIndex = rows.findIndex((row) => row.time === dragSelection.anchor);
    const currentIndex = rows.findIndex((row) => row.time === dragSelection.current);

    return rows
      .slice(Math.min(anchorIndex, currentIndex), Math.max(anchorIndex, currentIndex) + 1)
      .map((row) => row.time);
  };

  const isSelected = (date: string, time: string) =>
    !!selection && selection.date === date && selectedTimes(selection).includes(time);

  const handlePointerDown = (date: string, time: string) => {
    if (isSaving || !isEligible(date, time)) {
      return;
    }

    setSelection({ date, anchor: time, current: time });
  };

  const handlePointerEnter = (date: string, time: string) => {
    if (selection && selection.date === date) {
      setSelection({ ...selection, current: time });
    }
  };

  const handlePointerUp = () => {
    if (!selection || !calendar) {
      return;
    }

    const eligibleTimes = selectedTimes(selection).filter((time) => isEligible(selection.date, time));
    setSelection(null);

    if (eligibleTimes.length === 0) {
      return;
    }

    const change = {
      slots: eligibleTimes.map((time) => `${selection.date} ${time}`),
      status: nextAvailabilityStatus(calendar.slots[selection.date][selection.anchor].status),
    };

    if (change.slots.length === 1) {
      updateSlots({ technicianId: technician.ID, ...change });
      return;
    }

    setPendingChange(change);
  };

  const handleConfirmChange = () => {
    if (pendingChange) {
      updateSlots({ technicianId: technician.ID, ...pendingChange });
    }
    setPendingChange(null);
  };

  useEffect(() => {
    if (!selection) {
      return;
    }

    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelection(null);
      }
    };
    window.addEventListener('keydown', cancelOnEscape);

    return () => window.removeEventListener('keydown', cancelOnEscape);
  }, [selection]);

  const columns: ColumnsType<TimeRow> = [
    {
      key: 'time',
      dataIndex: 'time',
      title: '',
      width: 50,
      fixed: 'left',
      className: styles.availability__time,
      render: (time: string) => time.slice(0, 5),
    },
    ...days.map((day) => {
      const date = format(day, 'yyyy-MM-dd');

      return {
        key: date,
        title: (
          <div style={date === today ? { color: blue[5] } : undefined} data-testid={`day-${date}`}>
            <div className={styles.availability__weekday}>{format(day, 'EEE')}</div>
            <div>{format(day, 'do')}</div>
          </div>
        ),
        className: styles.availability__cell,
        onCell: (row: TimeRow) => ({
          onPointerDown: (event: PointerEvent<HTMLElement>) => event.button === 0 && handlePointerDown(date, row.time),
          onPointerEnter: () => handlePointerEnter(date, row.time),
          style: { cursor: !isSaving && isEligible(date, row.time) ? 'pointer' : 'default' },
        }),
        render: (_: unknown, row: TimeRow) => {
          const slot: AvailabilityCalendarSlot | undefined = calendar?.slots[date]?.[row.time];

          if (!slot) {
            return null;
          }

          const job = slotJob(slot);
          const content = (
            <div className={styles.availability__slot} data-testid={`slot-${date}-${row.time}`}>
              <Tile appearance={slotAppearance(slot)} />
              {isSelected(date, row.time) && (
                <div
                  className={styles.availability__selection}
                  style={{ background: blue[0], boxShadow: `inset 0 0 0 1px ${blue[5]}` }}
                />
              )}
            </div>
          );

          return job && hasJobPopover(slot) ? (
            <Popover
              trigger="hover"
              placement="right"
              open={selection ? false : undefined}
              content={<JobPopoverContent job={job} technician={technician} />}
            >
              {content}
            </Popover>
          ) : (
            content
          );
        },
      };
    }),
  ];

  return (
    <Flex vertical gap="middle" className={styles.availability}>
      <Flex justify="flex-end" gap="small">
        <Select
          aria-label="Month"
          value={month}
          options={MONTH_OPTIONS}
          onChange={setMonth}
          className={styles.availability__monthSelect}
        />
        <Select
          aria-label="Year"
          value={year}
          options={yearOptions}
          onChange={setYear}
          className={styles.availability__yearSelect}
        />
      </Flex>
      <div
        className={selection ? styles.availability__dragging : undefined}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => setSelection(null)}
      >
        <Table
          bordered
          size="small"
          rowKey="time"
          loading={isCalendarLoading}
          pagination={false}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 50 + 35 * days.length }}
          className={styles.availability__grid}
        />
      </div>
      <Flex vertical className={styles.availability__legend}>
        {LEGEND.map((row) => (
          <Flex key={row.title} align="center">
            <div className={styles.availability__legendTitle}>{row.title}</div>
            {row.items.map((item) => (
              <Flex
                key={item.label}
                align="center"
                gap={8}
                className={styles.availability__legendItem}
                style={{ width: LEGEND_ITEM_WIDTHS[item.label] ?? 120 }}
              >
                {item.appearance === 'recurring' ? (
                  <div className={styles.availability__legendIcon}>
                    <img src={repeatIcon} width={24} height={24} alt="" />
                  </div>
                ) : (
                  <div
                    className={`${styles.availability__legendIcon} ${
                      BORDERED_LEGEND_SWATCHES.includes(item.appearance) ? styles.availability__legendIcon_bordered : ''
                    }`}
                  >
                    <Tile appearance={item.appearance as SlotAppearance} />
                  </div>
                )}
                <span>{item.label}</span>
              </Flex>
            ))}
          </Flex>
        ))}
      </Flex>
      <Flex justify="flex-end" gap={8}>
        <Button.$
          type={Button.Type.Default}
          icon={<PlusOutlined />}
          onClick={() => setIsAwayStatusModalOpen(true)}
          dataTestId="add-away-status-button"
        >
          Add Away Status
        </Button.$>
        <Button.$
          type={Button.Type.Default}
          icon={<EditOutlined />}
          onClick={() => navigate(`/technician/edit/${technician.ID}/availability/away-status`)}
          dataTestId="edit-away-availability-button"
        >
          Edit Away Availability
        </Button.$>
        <Button.$
          type={Button.Type.Default}
          icon={<PlusOutlined />}
          onClick={() => setIsRecurringModalOpen(true)}
          dataTestId="add-recurring-availability-button"
        >
          Add Recurring Availability
        </Button.$>
        <Button.$
          type={Button.Type.Default}
          icon={<EditOutlined />}
          onClick={() => navigate(`/technician/edit/${technician.ID}/availability/recurring-availability`)}
          dataTestId="edit-recurring-availability-button"
        >
          Edit Recurring Availability
        </Button.$>
      </Flex>
      <AwayStatusModal
        isOpen={isAwayStatusModalOpen}
        isLoading={isAwayStatusCreating}
        onSubmit={handleAwayStatusSubmit}
        onClose={() => setIsAwayStatusModalOpen(false)}
      />
      <RecurringAvailabilityModal
        isOpen={isRecurringModalOpen}
        isLoading={isRecurringAvailabilityCreating}
        onSubmit={handleRecurringAvailabilitySubmit}
        onClose={() => setIsRecurringModalOpen(false)}
      />
      <Modal.$
        dataTestId="change-availability-status-modal"
        open={!!pendingChange}
        title="Change Availability Status"
        width={600}
        okText="Yes"
        cancelText="Cancel"
        onOk={handleConfirmChange}
        onCancel={() => setPendingChange(null)}
      >
        Are you sure you want to mark these time slots as {pendingChange && STATUS_LABELS[pendingChange.status]}?
      </Modal.$>
    </Flex>
  );
};
